import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useConfig } from '../providers/config'

const defaultCountriesValue = []

export const useCountries = () => {
	const { formatMessage } = useIntl()

	const { allCountries = defaultCountriesValue, shippingCountries = defaultCountriesValue } = useConfig()

	const sortAndTranslateCountries = useCallback(
		(countries) => {
			return countries
				.map(({ code, regions, ...country }) => ({
					...country,
					code,
					label: formatMessage({ id: 'country_' + code }),
					regions: regions?.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)),
				}))
				.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0))
		},
		[formatMessage]
	)

	const allCountriesSortedAndTranslated = useMemo(
		() => sortAndTranslateCountries(allCountries),
		[allCountries, sortAndTranslateCountries]
	)

	const shippingCountriesSortedAndTranslated = useMemo(
		() => sortAndTranslateCountries(shippingCountries),
		[shippingCountries, sortAndTranslateCountries]
	)

	const getCountryByCode = useCallback(
		(countryCode) => sortAndTranslateCountries(allCountries).find((country) => country.code === countryCode),
		[allCountries, sortAndTranslateCountries]
	)

	/**
	 * @description Given a country code and a region code or id, return the region name.
	 * @param {string} countryCode - The country code.
	 * @param {string} regionCode - The region code.
	 * @param {('code'|'id')} [type=code] - The type of regionCode passed.
	 * @returns {string|null}
	 */
	const getRegionByCode = useCallback(
		(countryCode, regionCode, type = 'code') =>
			sortAndTranslateCountries(allCountries)
				.find((country) => country.code === countryCode)
				?.regions?.find((region) => region[type] == regionCode)?.name,

		[allCountries, sortAndTranslateCountries]
	)

	return {
		allCountries: allCountriesSortedAndTranslated,
		shippingCountries: shippingCountriesSortedAndTranslated,
		getCountryByCode,
		getRegionByCode,
	}
}
